import VErrorImport from 'verror';

// VError is exported as both a type and a value, we need both
const VErrorConstructor = (VErrorImport as any).VError || VErrorImport;

export interface HttpErrorOptions {
  cause?: Error;
  info?: Record<string, unknown>;
  statusCode?: number;
  code?: string;
  message?: string;
}

export interface RestifyHttpErrorOptions extends HttpErrorOptions {
  retryable?: boolean;
}

/**
 * Base HttpError class that extends VError.
 * Compatible with restify-errors API.
 *
 * Supports multiple constructor signatures:
 * 1) new HttpError('my error message')
 * 2) new HttpError({statusCode: 500, message: 'error'})
 * 3) new HttpError(cause, 'additional message')
 * 4) new HttpError(cause, {statusCode: 500, message: 'error'})
 */
export class HttpError extends VErrorConstructor {
  name: string;

  message: string;

  statusCode: number;

  code: string;

  body: {
    code: string;
    message: string;
  };

  constructor(...args: any[]) {
    // Parse constructor arguments
    const parsed = HttpError.parseArgs(args);

    // Call VError constructor with proper arguments
    // Note: We do NOT pass the message string to VError constructor because
    // VError treats it as a printf-style format string, which would interpret
    // % characters as format specifiers and cause errors.
    // Instead, we only pass cause/info options and set the message afterwards.
    const vErrorOptions: any = {};
    if (parsed.cause) {
      vErrorOptions.cause = parsed.cause;
    }
    if (parsed.info && Object.keys(parsed.info).length > 0) {
      vErrorOptions.info = parsed.info;
    }

    super(vErrorOptions);

    // Fix prototype chain for instanceof to work correctly
    // This is needed when extending Error or VError in TypeScript
    Object.setPrototypeOf(this, new.target.prototype);

    // Set name for stack traces
    this.name = this.constructor.name;

    // Set message directly to avoid VError's printf-style interpretation
    this.message = parsed.message || '';

    // Set status code (use provided or default from prototype)
    this.statusCode =
      parsed.statusCode ||
      (this.constructor as typeof HttpError).prototype.statusCode ||
      500;

    // Set code (HTTP status name - use prototype default or derive from name)
    // Note: this.code is for HTTP status (e.g., 'Error', 'BadRequest')
    // while body.code can be customized (e.g., 'ik_request_body_mismatch')
    // IMPORTANT: If a custom code is provided in options, it should be set on the instance
    // to maintain compatibility with restify-errors behavior
    this.code =
      parsed.code ||
      (this.constructor as typeof HttpError).prototype.code ||
      this.name.replace(/Error$/, '');

    // Set body for serialization
    // body.code uses the custom code if provided, otherwise falls back to the error name
    // This matches restify-errors behavior where:
    // - new BadRequestError({code: 'custom_code'}) -> body.code = 'custom_code'
    // - new BadRequestError('message') -> body.code = 'BadRequest'
    // Note: body.code does NOT use the prototype code, it derives from the name
    // This is different from this.code which does use the prototype
    const bodyCode = parsed.code || this.name.replace(/Error$/, '');
    this.body = {
      code: bodyCode,
      message: parsed.message || '',
    };
  }

  /**
   * Parse constructor arguments into a normalized format.
   * Handles variadic signatures similar to restify-errors.
   */
  private static parseArgs(args: unknown[]): {
    cause?: Error;
    info?: Record<string, unknown>;
    statusCode?: number;
    code?: string;
    message?: string;
  } {
    if (args.length === 0) {
      return { message: '' };
    }

    const first = args[0];
    const second = args[1];

    // Case 1: First arg is an Error (cause)
    if (first instanceof Error) {
      if (
        typeof second === 'object' &&
        second !== null &&
        !Array.isArray(second)
      ) {
        const opts = second as HttpErrorOptions;
        return {
          cause: first,
          info: opts.info,
          statusCode: opts.statusCode,
          code: opts.code,
          message: opts.message || first.message,
        };
      }
      if (typeof second === 'string') {
        return {
          cause: first,
          message: second,
        };
      }
      return {
        cause: first,
        message: first.message,
      };
    }

    // Case 2: First arg is options object
    if (typeof first === 'object' && first !== null && !Array.isArray(first)) {
      const opts = first as HttpErrorOptions;
      // If second arg is a string, it overrides the message from options
      // This handles parseWithError signature: new Error(options, zodMessage)
      const message = typeof second === 'string' ? second : opts.message || '';
      return {
        cause: opts.cause,
        info: opts.info,
        statusCode: opts.statusCode,
        code: opts.code,
        message,
      };
    }

    // Case 3: First arg is a string message
    if (typeof first === 'string') {
      return {
        message: first,
      };
    }

    return { message: '' };
  }

  /**
   * Serialize error to JSON format.
   * Compatible with restify-errors toJSON behavior.
   */
  toJSON(): { code: string; message: string } {
    let { message } = this.body;

    // If we have a cause, get the full error chain message
    const causeMethod = (this as any).cause;
    if (causeMethod && typeof causeMethod === 'function') {
      try {
        const causeError = causeMethod();
        if (causeError) {
          const fullString = this.toString();
          const colonIndex = fullString.indexOf(':');
          if (colonIndex !== -1) {
            message = fullString.substring(colonIndex + 1).trim();
          }
        }
      } catch {
        // If there's no cause (VError throws when calling cause() with no cause),
        // just use the message from body
      }
    }

    return {
      code: this.body.code,
      message,
    };
  }

  /**
   * Get context/info object (compatibility with restify-errors)
   */
  get context(): Record<string, unknown> {
    return VErrorConstructor.info(this);
  }
}

// Set default prototype values
HttpError.prototype.name = 'HttpError';
HttpError.prototype.statusCode = 500;
HttpError.prototype.code = 'Error';
